import { ReactNode } from "react";
import { Message } from "primereact/message";
import { useTenantScope } from "../../hooks/useTenantScope";

/**
 * SYSTEM_DESIGN.md §6.4 / INC-05 — Creación con scope de tenant.
 *
 * El §6.4 prohíbe el campo de Tenant dentro de los formularios: se hereda del
 * selector global del header. Cuando el header está en "Todos" no hay tenant al
 * que asignar la entidad, así que la creación se bloquea con un banner
 * explicativo — no con un botón gris y sin motivo.
 *
 * Reemplaza a `TenantSectionPicker` + `useSectionTenant`, que resolvían el caso
 * ofreciendo un dropdown local de tenant — exactamente el tercer mecanismo que
 * INC-05 viene a eliminar. Se dieron de baja al migrar Beneficios.
 */
/** Tooltip del botón deshabilitado. Ahí la acción bloqueada siempre es crear. */
export const TENANT_GATE_MESSAGE = "Seleccioná un tenant en el header para crear";

/** Titular del banner. No dice "para crear" porque no siempre es solo crear lo
 *  que queda bloqueado: la frase concreta la aporta `action`. */
const TENANT_GATE_HEADING = "Seleccioná un tenant en el header";

export interface TenantGate {
  /** false cuando el header está en "Todos" y no hay tenant al que asignar. */
  allowed: boolean;
  /** Tenant que se va a heredar. null si el scope es global. */
  tenantId: string | null;
  /** Motivo del bloqueo, para el tooltip del botón deshabilitado. */
  reason?: string;
  loading: boolean;
}

export const useTenantGate = (): TenantGate => {
  const scope = useTenantScope();

  if (scope.loading) {
    return { allowed: false, tenantId: null, loading: true };
  }

  // TENANT_ADMIN siempre tiene su tenant fijado; solo SUPER_ADMIN puede estar
  // en scope global.
  const allowed = scope.tenantId !== null;

  return {
    allowed,
    tenantId: scope.tenantId,
    loading: false,
    reason: allowed ? undefined : TENANT_GATE_MESSAGE,
  };
};

/**
 * Banner de bloqueo. No renderiza nada cuando la creación está habilitada.
 */
export const TenantGateBanner = ({
  action = "continuar",
  gate,
}: {
  /**
   * Qué queda habilitado al elegir un tenant, como frase verbal completa:
   * "crear el evento", "ver y crear categorías".
   *
   * Es la acción entera y no solo la entidad porque con "de {entity}" salía
   * "de el evento", y porque no en todas las pantallas lo que se bloquea es
   * crear: en Categorías se bloquea también el listado.
   */
  action?: string;
  gate: TenantGate;
}) => {
  if (gate.loading || gate.allowed) return null;

  return (
    <Message
      severity="warn"
      className="w-full justify-content-start mb-4"
      content={
        <div className="flex align-items-start gap-2">
          <i className="pi pi-exclamation-triangle mt-1" />
          <div>
            {/* Sin detalles de implementación: que el formulario "herede el valor
                del header" es cierto pero le habla al que programó la pantalla,
                no a quien la usa. */}
            <b>{TENANT_GATE_HEADING}.</b>
            <br />
            El selector global está en «Todos». Elegí un tenant específico para {action}.
          </div>
        </div>
      }
    />
  );
};

/**
 * Deshabilita todo lo que envuelve mientras la creación esté bloqueada.
 *
 * Es prevención en vez de aviso: si el formulario no se puede completar sin un
 * tenant elegido, no hay nada que perder cuando el usuario lo cambie en el
 * header (cambiarlo recarga la pantalla). Antes el formulario se podía cargar
 * entero y recién el botón de guardar estaba deshabilitado.
 *
 * `fieldset[disabled]` desactiva los controles nativos y los saca del orden de
 * tabulación. `pointer-events: none` cubre lo que el fieldset no alcanza: los
 * componentes de PrimeReact que son <div> con un input oculto — un Dropdown
 * seguiría abriendo su panel al hacerle clic.
 */
export const TenantGateLock = ({
  gate,
  children,
}: {
  gate: TenantGate;
  children: ReactNode;
}) => {
  const bloqueado = !gate.loading && !gate.allowed;

  return (
    <fieldset
      disabled={bloqueado}
      className={`tenant-gate-lock ${bloqueado ? "tenant-gate-lock--locked" : ""}`}
      aria-disabled={bloqueado}
    >
      {children}
    </fieldset>
  );
};
